import { fetchSamplingDate } from './utils';
import { RISK_COUNT_TREND_METRICS_FLAGS } from './constants';

interface RiskData {
  event_timestamp: string;
  issue_detected: string;
}

interface AsrData {
  application_certification_status: string;
}

interface SelectedManager {
  label: string;
}

interface RiskRemediatorAggregatedData {
  remediatedRiskData: RiskData[];
  openRiskData: RiskData[];
  devRemediatedRiskData: RiskData[];
  asrData: AsrData[];
  selectedManager?: SelectedManager;
  duration: string;
}

export const aggregateRiskRemediatorSummaryData = (riskRemediatorData: RiskRemediatorAggregatedData) => {
  // Aggregate Risk Remediator data for AI summary - overview page metrics, charts, and table data
  const totalRisks =
    riskRemediatorData.remediatedRiskData.length + riskRemediatorData.openRiskData.length + riskRemediatorData.devRemediatedRiskData.length;
  const autoRemediationRate = totalRisks > 0 ? ((riskRemediatorData.remediatedRiskData.length / totalRisks) * 100).toPrecision(4) : 0;
  const asrCertificationRate =
    riskRemediatorData.asrData.length > 0
      ? (
          (riskRemediatorData.asrData.filter((obj: AsrData) => obj.application_certification_status == 'CERTIFIED').length /
            riskRemediatorData.asrData.length) *
          100
        ).toPrecision(4)
      : 0;

  // Handle missing duration
  const duration = riskRemediatorData.duration || 'Unknown';
  const selectedManager = riskRemediatorData.selectedManager?.label || 'Unknown';

  // Use existing chart data functions to get aggregated data - eliminates code duplication and ensures consistency
  const trendChartData = aggregateRiskTrendChartData(riskRemediatorData);
  const riskCategoryChartData = aggregateRiskCategoryChartData(riskRemediatorData);
  const asrChartData = aggregateAsrChartData(riskRemediatorData);

  return {
    metrics: {
      totalRisks,
      openRisks: riskRemediatorData.openRiskData.length,
      autoRemediated: riskRemediatorData.remediatedRiskData.length,
      devRemediated: riskRemediatorData.devRemediatedRiskData.length,
      autoRemediationRate,
      asrCertificationRate
    },
    // Include chart data for comprehensive AI analysis
    chartData: {
      trendData: trendChartData,
      riskCategoryData: riskCategoryChartData,
      asrData: asrChartData
    },
    selectedManager: selectedManager,
    duration: duration
  };
};

// Chart data aggregation functions - reusing the same logic as the chart functions in RiskRemediatorOverviewPage
export const aggregateRiskTrendChartData = (riskRemediatorData: any) => {
  // Handle missing or invalid duration
  if (!riskRemediatorData.duration || !RISK_COUNT_TREND_METRICS_FLAGS[riskRemediatorData.duration]) {
    return [];
  }

  const frequency = RISK_COUNT_TREND_METRICS_FLAGS[riskRemediatorData.duration].frequency;
  const totalDays = RISK_COUNT_TREND_METRICS_FLAGS[riskRemediatorData.duration].totalDays;
  const trendData = [];

  for (let i = 0; i <= totalDays; i = i + frequency) {
    const filterDate = fetchSamplingDate(i);
    const prevFilterDate = fetchSamplingDate(i + frequency, 'Previous');
    const openRisk = riskRemediatorData.openRiskData.filter(
      (obj: Record<string, string>) => obj.event_timestamp <= filterDate && obj.event_timestamp > prevFilterDate
    ).length;
    const remediatedRisk = riskRemediatorData.remediatedRiskData.filter(
      (obj: Record<string, string>) => obj.event_timestamp <= filterDate && obj.event_timestamp > prevFilterDate
    ).length;
    const devRemediatedRisk = riskRemediatorData.devRemediatedRiskData.filter(
      (obj: Record<string, string>) => obj.event_timestamp <= filterDate && obj.event_timestamp > prevFilterDate
    ).length;

    trendData.push({
      period: filterDate,
      openRisks: openRisk,
      autoRemediated: remediatedRisk,
      devRemediated: devRemediatedRisk,
      totalNonAuto: devRemediatedRisk + openRisk
    });
  }

  return trendData;
};

export const aggregateRiskCategoryChartData = (riskRemediatorData: any) => {
  const aggregatedRiskCategories: Record<string, any> = {};
  const riskList = [riskRemediatorData.openRiskData, riskRemediatorData.remediatedRiskData, riskRemediatorData.devRemediatedRiskData];

  riskList.forEach((riskData: Record<string, string>[]) => {
    const riskCategories = [...new Set(riskData.map((obj: any) => obj.issue_detected))];
    riskCategories.forEach((category: string) => {
      const count = riskData.filter((obj: Record<string, string>) => obj.issue_detected == category).length;
      aggregatedRiskCategories[category] = (aggregatedRiskCategories[category] || 0) + count;
    });
  });

  return Object.entries(aggregatedRiskCategories).map(([category, count]) => ({
    category,
    count
  }));
};

export const aggregateAsrChartData = (riskRemediatorData: any) => {
  const aggregatedAppCategories: Record<string, any> = {};

  riskRemediatorData.asrData.forEach((asrApp: Record<string, string>) => {
    const certificationStatus = asrApp.application_certification_status?.replace(/_/g, ' ');
    const count = riskRemediatorData.asrData.filter(
      (obj: Record<string, string>) => obj.application_certification_status?.replace(/_/g, ' ') == certificationStatus
    ).length;
    aggregatedAppCategories[certificationStatus] = count;
  });

  return Object.entries(aggregatedAppCategories).map(([status, count]) => ({
    status,
    count
  }));
};
